import { useState } from 'react'
import { Link } from 'react-router-dom'

import { NICHT_VORHANDEN, api, ohne404 } from '../api'
import { datum, zeitpunkt } from '../format'
import { useAktion, useLaden } from '../hooks'
import { SCHWERE } from '../texte'
import Meldung from './Meldung'
import Meldungen from './Meldungen'
import type { Befund, Datenpruefung as DatenpruefungTyp, DatenpruefungZaehler, Quittung } from '../typen'

/** Bis hierhin stehen Artikelnummern in der Zeile; der Rest wird gezählt. */
const ARTIKEL_MAX = 20

/** „2 Fehler, 3 Warnungen, 1 Hinweis" - nur die Zähler über null; alle null: leer. */
export function zaehlerText(z: DatenpruefungZaehler): string {
  const teile: string[] = []
  if (z.fehler > 0) teile.push(`${z.fehler} Fehler`)
  if (z.warnung > 0) teile.push(`${z.warnung} ${z.warnung === 1 ? 'Warnung' : 'Warnungen'}`)
  if (z.hinweis > 0) teile.push(`${z.hinweis} ${z.hinweis === 1 ? 'Hinweis' : 'Hinweise'}`)
  return teile.join(', ')
}

/** „erledigt von Frau Barth am 02.11.2026: in SelectLine korrigiert" - ein Satz je Quittung. */
function quittungText(q: Quittung): string {
  const von = q.quittiert_von ? ` von ${q.quittiert_von}` : ''
  const vorlage = q.wiedervorlage ? ` - wieder vorlegen am ${datum(q.wiedervorlage)}` : ''
  return `erledigt${von} am ${datum(q.quittiert_am)}: ${q.grund}${vorlage}`
}

/**
 * Der Kasten „Datenprüfung" auf „Heute" (18.09.2026): das Backend prüft die Daten regelbasiert
 * (Artikel ohne Gruppe, Bestände ohne Verbrauch, doppelte Kopplungen …) und liefert Befunde mit
 * Titel, Text, Artikelnummern und dem Weg zur Stelle. Die Zähler stehen als Wort mit Zahl; leer
 * heißt „Keine Befunde".
 *
 * Seit dem 02.11.2026 mit Gedächtnis: Frau Barth hatte Fehler, Warnung und Hinweis bearbeitet, und
 * sie standen trotzdem da - „Woran erkenne ich, dass sich da schon jemand drum gekümmert hat?" Jetzt
 * trägt jeder Befund „Erledigt" mit Pflicht-Grund (für den ganzen Befund oder einzelne Artikel), und
 * was erledigt ist, steht eingeklappt darunter mit wer, wann, warum und „Wieder öffnen". Gezählt
 * wird nur Offenes; `onGeaendert` lässt die Kachel oben auf „Heute" mitzählen.
 */
export default function Datenpruefung({ onGeaendert }: { onGeaendert?: () => void } = {}) {
  const { daten: geholt, fehler, neuLaden } = useLaden<DatenpruefungTyp | typeof NICHT_VORHANDEN>(() => ohne404(api.datenpruefung()), [])
  const aktion = useAktion()
  // Welcher Befund gerade sein „Erledigt"-Formular offen hat - immer nur einer, damit es ruhig bleibt.
  const [offen, setOffen] = useState<string | null>(null)
  // Ein Backend ohne diesen Endpunkt ist kein Fehler: dann gehört die Karte gar nicht auf die Seite.
  if (geholt === NICHT_VORHANDEN) return null
  const daten = geholt

  function geaendert() {
    neuLaden()
    onGeaendert?.()
  }

  function wiederOeffnen(q: Quittung) {
    void aktion.ausfuehren(async () => {
      await api.quittungAufheben(q.id)
      geaendert()
    }, `Wieder geöffnet${q.artikelnummer ? ` (Artikel ${q.artikelnummer})` : ''}.`)
  }

  // Erledigt sind ganze Befunde und einzelne Artikel offener Befunde - beides mit seinen Quittungen.
  const mitQuittungen = [...(daten?.erledigt ?? []), ...(daten?.befunde ?? [])].filter((b) => (b.quittiert ?? []).length > 0)
  const anzahlQuittungen = mitQuittungen.reduce((summe, b) => summe + (b.quittiert ?? []).length, 0)

  return (
    <div className="karte datenpruefung" id="datenpruefung">
      <div className="leiste">
        <b>Datenprüfung</b>
        {daten && <span className="hinweis">{zaehlerText(daten.zaehler) || 'nichts offen'}, geprüft {zeitpunkt(daten.geprueft_am)}</span>}
      </div>
      {fehler && <Meldung art="fehler">Die Datenprüfung ließ sich nicht laden: {fehler}</Meldung>}
      <Meldungen fehler={aktion.fehler} erfolg={aktion.erfolg} />
      {!daten && !fehler && <p className="hinweis">Lädt…</p>}
      {daten && daten.befunde.length === 0 && (
        <p className="hinweis">{anzahlQuittungen > 0 ? 'Keine offenen Befunde.' : 'Keine Befunde.'}</p>
      )}
      {daten && daten.befunde.length > 0 && (
        <ul className="befund-liste">
          {daten.befunde.map((b) => (
            <li key={b.schluessel} className={`befund ${b.schwere}`}>
              <span className={`status ${b.schwere}`}>{SCHWERE[b.schwere]}</span>
              <div className="befund-text">
                <b>{b.titel}</b>
                {b.anzahl > 0 && <span className="hinweis"> ({b.anzahl})</span>}
                <div>{b.text}</div>
                {b.artikel.length > 0 && (
                  <div className="hinweis">
                    {b.artikel.slice(0, ARTIKEL_MAX).join(', ')}
                    {b.artikel.length > ARTIKEL_MAX ? ` und ${b.artikel.length - ARTIKEL_MAX} weitere` : ''}
                  </div>
                )}
                <div className="befund-knoepfe">
                  {b.link && (
                    <Link className="hinweis-link" to={b.link}>
                      öffnen
                    </Link>
                  )}
                  {offen !== b.schluessel && (
                    <button className="leise" disabled={aktion.arbeitet} onClick={() => setOffen(b.schluessel)}>
                      Erledigt…
                    </button>
                  )}
                </div>
                {offen === b.schluessel && (
                  <Erledigen
                    befund={b}
                    onFertig={(gespeichert) => {
                      setOffen(null)
                      if (!gespeichert) return
                      aktion.setErfolg(`„${b.titel}“ als erledigt vermerkt - steht unten unter „Erledigt“.`)
                      geaendert()
                    }}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {anzahlQuittungen > 0 && (
        <details className="befund-erledigt">
          <summary>Erledigt ({anzahlQuittungen})</summary>
          <ul className="befund-liste">
            {mitQuittungen.map((b) => (
              <li key={b.schluessel} className="befund">
                <span className="status erledigt">erledigt</span>
                <div className="befund-text">
                  <b>{b.titel}</b>
                  <ul className="quittungen">
                    {(b.quittiert ?? []).map((q) => (
                      <li key={q.id}>
                        {q.artikelnummer ? <>Artikel {q.artikelnummer}: </> : <>ganzer Befund: </>}
                        <span className="hinweis">{quittungText(q)}</span>{' '}
                        <button className="leise" disabled={aktion.arbeitet} onClick={() => wiederOeffnen(q)}>
                          Wieder öffnen
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}

/**
 * Das kleine Formular unter einem Befund: Grund (Pflicht), auf Wunsch eine Wiedervorlage und - bei
 * mehreren Artikeln - welche erledigt sind. Alle angehakt heißt: der ganze Befund.
 */
function Erledigen({ befund, onFertig }: { befund: Befund; onFertig: (gespeichert: boolean) => void }) {
  const [grund, setGrund] = useState('')
  const [wiedervorlage, setWiedervorlage] = useState('')
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(() => new Set(befund.artikel))
  const { fehler, arbeitet, setFehler, ausfuehren } = useAktion()
  const mitAuswahl = befund.artikel.length > 1
  const alle = gewaehlt.size === befund.artikel.length

  function umschalten(nummer: string, an: boolean) {
    setGewaehlt((alt) => {
      const neu = new Set(alt)
      if (an) neu.add(nummer)
      else neu.delete(nummer)
      return neu
    })
  }

  async function speichern() {
    if (!grund.trim()) {
      setFehler('Bitte kurz sagen, was getan wurde oder warum es so bleiben darf - das sehen die anderen später.')
      return
    }
    if (mitAuswahl && gewaehlt.size === 0) {
      setFehler('Bitte mindestens einen Artikel anhaken.')
      return
    }
    const ergebnis = await ausfuehren(() =>
      api.quittieren({
        schluessel: befund.schluessel,
        // Alle angehakt: der ganze Befund - auch die Artikel, die nur gezählt und nicht genannt sind.
        artikel: mitAuswahl && !alle ? befund.artikel.filter((a) => gewaehlt.has(a)) : [],
        grund: grund.trim(),
        wiedervorlage: wiedervorlage || null,
      }),
    )
    if (ergebnis !== undefined) onFertig(true)
  }

  return (
    <form
      className="befund-formular"
      onSubmit={(e) => {
        e.preventDefault()
        void speichern()
      }}
    >
      {mitAuswahl && (
        <div className="befund-artikelwahl">
          <span className="hinweis">Erledigt für:</span>{' '}
          <button type="button" className="leise" onClick={() => setGewaehlt(alle ? new Set() : new Set(befund.artikel))}>
            {alle ? 'keinen' : 'alle'}
          </button>
          <div className="befund-artikel">
            {befund.artikel.map((a) => (
              <label key={a} className="haekchen">
                <input type="checkbox" checked={gewaehlt.has(a)} onChange={(e) => umschalten(a, e.target.checked)} /> {a}
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="leiste" style={{ marginBottom: 0 }}>
        <label className="feld befund-grund">
          <span className="hinweis">Was wurde getan, oder warum bleibt es so? *</span>
          <input
            type="text"
            value={grund}
            autoFocus
            placeholder="z. B. in SelectLine korrigiert / ist so gewollt, IBC ohne Verpackung"
            onChange={(e) => setGrund(e.target.value)}
          />
        </label>
        <label className="feld">
          <span className="hinweis">wieder vorlegen am (optional)</span>
          <input type="date" value={wiedervorlage} onChange={(e) => setWiedervorlage(e.target.value)} />
        </label>
        <button className="haupt" type="submit" disabled={arbeitet}>
          {mitAuswahl && !alle ? `Für ${gewaehlt.size} Artikel erledigt` : 'Als erledigt vermerken'}
        </button>
        <button type="button" disabled={arbeitet} onClick={() => onFertig(false)}>
          Abbrechen
        </button>
      </div>
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}
    </form>
  )
}
